import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Icon, Label} from '@forecast-it/design-system';
import {C, F} from './versionsStyles';

export const TEMPLATE_LIST_MAX = 5;

export interface TemplateOption {
	readonly id: string;
	readonly name: string | null;
	readonly phaseCount: number | null;
	readonly totalMinutes: number | null;
}

export interface TemplateChoiceProps {
	templates: ReadonlyArray<TemplateOption>;
	value: string | null;
	onChange: (id: string) => void;
}

const Column = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

const InlineRow = styled.button<{$selected: boolean}>`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 10px;
	font: inherit;
	text-align: left;
	cursor: pointer;
	color: ${C.text};
	border: 1px solid ${p => (p.$selected ? C.brand : C.border)};
	border-radius: 4px;
	background: ${p => (p.$selected ? C.brandTint : 'transparent')};
	&:focus-visible {
		outline: 2px solid ${C.brand};
		outline-offset: 1px;
	}
`;

const Name = styled.span`
	${F.body2}
	flex: 1;
	min-width: 0;
`;

const Detail = styled.span`
	${F.detail}
	color: ${C.aux};
	flex: none;
`;

const Field = styled.div<{$active: boolean}>`
	display: flex;
	align-items: center;
	min-height: 40px;
	box-sizing: border-box;
	background: ${C.surface};
	border: 1px solid ${p => (p.$active ? C.brand : C.border)};
	border-radius: 4px;
	box-shadow: ${p => (p.$active ? `0 0 0 1px ${C.brand}` : 'none')};
`;

const SearchIcon = styled.span`
	display: flex;
	padding-left: 10px;
	flex: none;
`;

const Input = styled.input`
	${F.body2}
	flex: 1;
	min-width: 0;
	border: none;
	outline: none;
	background: transparent;
	color: ${C.text};
	padding: 8px;
`;

const Chevron = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	align-self: stretch;
	width: 36px;
	flex: none;
	border: none;
	border-left: 1px solid ${C.border};
	background: transparent;
	cursor: pointer;
	padding: 0;
`;

const Listbox = styled.div`
	position: fixed;
	z-index: 1000;
	max-height: 280px;
	overflow-y: auto;
	background: ${C.surface};
	border: 1px solid ${C.border};
	border-radius: 4px;
	box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
	padding: 4px 0;
`;

const Option = styled.div<{$highlighted: boolean}>`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px 12px;
	cursor: pointer;
	background: ${p => (p.$highlighted ? C.brandTint : 'transparent')};
`;

const Tick = styled.span`
	display: flex;
	flex: none;
	width: 16px;
`;

const Match = styled.span`
	${F.sub2}
	color: ${C.brand};
`;

const Empty = styled.div`
	${F.body2}
	padding: 10px 12px;
	color: ${C.aux};
`;

const byName = (a: TemplateOption, b: TemplateOption) =>
	(a.name ?? '').localeCompare(b.name ?? '', undefined, {sensitivity: 'base'});

let nextId = 0;

export const TemplateChoice = ({templates, value, onChange}: TemplateChoiceProps) => {
	const {formatMessage} = useIntl();
	const baseId = useMemo(() => `template-choice-${++nextId}`, []);
	const listboxId = `${baseId}-list`;
	const optionId = (i: number) => `${baseId}-option-${i}`;
	const sorted = useMemo(() => [...templates].sort(byName), [templates]);
	const picked = sorted.find(t => t.id === value) ?? null;
	const pickedName = picked?.name ?? '';

	const [query, setQuery] = useState(pickedName);
	const [open, setOpen] = useState(false);
	const [highlighted, setHighlighted] = useState(0);
	const [focused, setFocused] = useState(false);
	const [rect, setRect] = useState<DOMRect | null>(null);
	const fieldRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		setQuery(pickedName);
	}, [pickedName]);

	const measure = () => {
		if (fieldRef.current) {
			setRect(fieldRef.current.getBoundingClientRect());
		}
	};
	const show = () => {
		measure();
		setHighlighted(
			Math.max(
				0,
				sorted.findIndex(t => t.id === value)
			)
		);
		setOpen(true);
	};

	const leaveField = (e: React.FocusEvent) => {
		const next = e.relatedTarget as Node | null;
		if (next && (fieldRef.current?.contains(next) || listRef.current?.contains(next))) {
			return;
		}
		setOpen(false);
		setQuery(pickedName);
	};

	useLayoutEffect(() => {
		if (!open) {
			return undefined;
		}
		window.addEventListener('resize', measure);
		window.addEventListener('scroll', measure, true);
		return () => {
			window.removeEventListener('resize', measure);
			window.removeEventListener('scroll', measure, true);
		};
	}, [open]);

	useEffect(() => {
		if (!open) {
			return undefined;
		}
		const onDown = (e: MouseEvent) => {
			const target = e.target as Node;
			if (fieldRef.current?.contains(target) || listRef.current?.contains(target)) {
				return;
			}
			setOpen(false);
			setQuery(pickedName);
		};
		document.addEventListener('mousedown', onDown);
		return () => document.removeEventListener('mousedown', onDown);
	}, [open, pickedName]);

	if (sorted.length <= TEMPLATE_LIST_MAX) {
		return (
			<Column
				role="radiogroup"
				aria-label={formatMessage({id: 'baseline.versions.template_label'}, {count: sorted.length})}
			>
				{sorted.map(t => {
					const selected = t.id === value;
					return (
						<InlineRow
							key={t.id}
							type="button"
							role="radio"
							aria-checked={selected}
							$selected={selected}
							onClick={() => onChange(t.id)}
						>
							<Icon icon={selected ? 'tick' : 'list'} size="s" color={selected ? 'primary' : 'default'} />
							<Name>{t.name}</Name>
							<Detail>
								{formatMessage({id: 'baseline.versions.template_phases'}, {count: t.phaseCount ?? 0})}
							</Detail>
						</InlineRow>
					);
				})}
			</Column>
		);
	}

	const q = query.trim().toLowerCase();
	const list = !q || query === pickedName ? sorted : sorted.filter(t => (t.name ?? '').toLowerCase().includes(q));

	const pick = (t: TemplateOption) => {
		onChange(t.id);
		setQuery(t.name ?? '');
		setOpen(false);
	};

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (open) {
				setHighlighted(Math.min(highlighted + 1, list.length - 1));
			} else {
				show();
			}
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setHighlighted(Math.max(highlighted - 1, 0));
		} else if (e.key === 'Enter') {
			if (open && list[highlighted]) {
				e.preventDefault();
				pick(list[highlighted]);
			}
		} else if (e.key === 'Escape') {
			setOpen(false);
			setQuery(pickedName);
		}
	};

	const mark = (name: string) => {
		if (!q || query === pickedName) {
			return name;
		}
		const i = name.toLowerCase().indexOf(q);
		if (i < 0) {
			return name;
		}
		return (
			<>
				{name.slice(0, i)}
				<Match>{name.slice(i, i + q.length)}</Match>
				{name.slice(i + q.length)}
			</>
		);
	};

	const searchText = formatMessage({id: 'baseline.versions.template_search'}, {count: sorted.length});

	return (
		<Column>
			<Label>{formatMessage({id: 'baseline.versions.template_label'}, {count: sorted.length})}</Label>
			<div>
				<Field ref={fieldRef} $active={focused || open}>
					<SearchIcon>
						<Icon icon="search" size="s" />
					</SearchIcon>
					<Input
						ref={inputRef}
						value={query}
						placeholder={searchText}
						role="combobox"
						aria-expanded={open}
						aria-label={searchText}
						aria-autocomplete="list"
						aria-controls={open ? listboxId : undefined}
						aria-activedescendant={open && list[highlighted] ? optionId(highlighted) : undefined}
						onFocus={() => {
							setFocused(true);
							show();
						}}
						onBlur={e => {
							setFocused(false);
							leaveField(e);
						}}
						onChange={e => {
							setQuery(e.target.value);
							if (!open) {
								show();
							}
							setHighlighted(0);
						}}
						onKeyDown={onKeyDown}
					/>
					<Chevron
						type="button"
						aria-label={formatMessage({id: open ? 'common.close' : 'common.show'})}
						onMouseDown={e => e.preventDefault()}
						onBlur={leaveField}
						onClick={() => {
							if (open) {
								setOpen(false);
							} else {
								inputRef.current?.focus();
								show();
							}
						}}
					>
						<Icon icon={open ? 'chevronUp' : 'chevronDown'} size="s" />
					</Chevron>
				</Field>
			</div>
			{open && rect
				? ReactDOM.createPortal(
						<Listbox
							ref={listRef}
							id={listboxId}
							role="listbox"
							onMouseDown={e => e.preventDefault()}
							style={{left: rect.left, top: rect.bottom + 4, width: Math.max(rect.width, 320)}}
						>
							{list.length ? (
								list.map((t, i) => (
									<Option
										key={t.id}
										id={optionId(i)}
										role="option"
										aria-selected={t.id === value}
										$highlighted={i === highlighted}
										onMouseDown={e => e.preventDefault()}
										onMouseEnter={() => setHighlighted(i)}
										onClick={() => pick(t)}
									>
										<Tick>{t.id === value ? <Icon icon="tick" size="s" color="primary" /> : null}</Tick>
										<Name>{mark(t.name ?? '')}</Name>
										<Detail>
											{formatMessage(
												{id: 'baseline.versions.template_phases'},
												{count: t.phaseCount ?? 0}
											)}
										</Detail>
									</Option>
								))
							) : (
								<Empty>{formatMessage({id: 'baseline.versions.template_no_match'}, {query})}</Empty>
							)}
						</Listbox>,
						document.body
				  )
				: null}
		</Column>
	);
};
